// PauseCard — the dialog shown while the game is auto-paused (pacing spec §4).
//   away:   "Welcome back" + how long the game was paused
//   danger: one row per alert, each with "Go to" (pans the camera there)
// Centred over a dimmed, blurred backdrop, with cover art in the header:
// the player's dominant doctrine or a random realm scene (away), or a siege
// scene (danger) — see pauseCovers.ts. Type and icons scale with the
// viewport (clamp). Space / Enter resumes, like the Resume button. The card
// follows the app theme (its body is the themed card surface); the cover art
// keeps a dark scrim under the title so the white title stays legible over
// the picture in either theme.

import { Button } from '@aod/ui/components/ui/button';
import { Coffee, Crosshair, Flag, Play, ShieldAlert, Swords } from 'lucide-react';
import { type CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import type { Archetype } from '../ui/DoctrinePanel/archetypes.js';
import type { DangerAlert } from './detectDangers.js';
import { pickPauseCover } from './pauseCovers.js';
import type { PauseCard as PauseCardState } from './usePauseController.js';

interface PauseCardProps {
  readonly card: PauseCardState;
  readonly onResume: () => void;
  readonly onGoTo: (position: [number, number, number]) => void;
  /** The local player's dominant doctrine — picks the "away" cover art. */
  readonly dominant?: Archetype | null;
}

/** The card is informational — it auto-closes (and the game resumes) after
 * this long, fading out over FADE_MS. Any interaction cancels the timers. */
const AUTO_CLOSE_MS = 5000;
const FADE_MS = 1000;

export function formatAway(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s} second${s === 1 ? '' : 's'}`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} minute${m === 1 ? '' : 's'}`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest === 0 ? `${h} hour${h === 1 ? '' : 's'}` : `${h} h ${rest} min`;
}

export function describeAlert(a: DangerAlert): string {
  if (a.kind === 'captured') {
    return `${a.cityName} was captured${a.newOwner !== undefined ? ` by Player ${a.newOwner + 1}` : ''}!`;
  }
  return `${a.cityName} is under attack${a.damage ? ` (−${a.damage} HP)` : ''}`;
}

export function PauseCard({ card, onResume, onGoTo, dominant = null }: PauseCardProps) {
  // Auto-close: fade out after AUTO_CLOSE_MS, then actually resume. A
  // ref keeps the latest callback without restarting the timers on rerenders.
  const [fading, setFading] = useState(false);
  const onResumeRef = useRef(onResume);
  onResumeRef.current = onResume;
  useEffect(() => {
    const t1 = setTimeout(() => setFading(true), AUTO_CLOSE_MS);
    const t2 = setTimeout(() => onResumeRef.current(), AUTO_CLOSE_MS + FADE_MS);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        onResume();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onResume]);

  const danger = card.kind === 'danger';
  // Pick once per card: a new cover each time the game pauses, stable while open.
  const captured = danger && card.alerts.some((a) => a.kind === 'captured');
  const cover = useMemo(
    () => pickPauseCover(card.kind, dominant, Math.random, captured ? 'captured' : 'attacked'),
    [card.kind, dominant, captured],
  );
  const [imgFailed, setImgFailed] = useState(false);
  const title = danger
    ? captured
      ? 'A city has fallen'
      : 'Your realm is under attack'
    : 'Welcome back';
  const Icon = danger ? ShieldAlert : Coffee;
  const accentVars = { '--pause-accent': cover.accent } as CSSProperties;

  return (
    // biome-ignore lint/a11y/useSemanticElements: this div is the full-screen backdrop that centres the card; a native <dialog> would need a showModal() lifecycle and top-layer style resets
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="pause-card-title"
      id="pause-card"
      data-testid={`pause-card-${card.kind}`}
      data-appdom-id="pause-card"
      data-appdom-type="Modal"
      data-appdom-scope="dialog"
      data-appdom-label={title}
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-3 backdrop-blur-[3px] transition-opacity duration-1000 sm:p-6 ${
        fading ? 'opacity-0' : 'animate-fade-in-overlay opacity-100'
      }`}
      style={accentVars}
    >
      <section
        className="animate-fade-in-scale relative flex max-h-[94dvh] w-full max-w-[min(94vw,42rem)] flex-col overflow-hidden rounded-2xl border bg-card text-card-foreground"
        style={{
          borderColor: `color-mix(in srgb, ${cover.accent} 45%, transparent)`,
          boxShadow: `0 0 0 1px color-mix(in srgb, ${cover.accent} 20%, transparent), 0 32px 90px -24px color-mix(in srgb, ${cover.accent} 55%, black)`,
        }}
      >
        <header
          className="relative aspect-[16/9] max-h-[46dvh] w-full shrink-0 overflow-hidden"
          data-testid="pause-card-cover"
          data-cover={cover.id}
          style={{
            // Shown while the art loads, and if it fails to.
            background: `radial-gradient(120% 90% at 30% 10%, color-mix(in srgb, ${cover.accent} 55%, #1c1d2b), #0d0e16)`,
          }}
        >
          {!imgFailed && (
            <img
              src={cover.src}
              srcSet={cover.srcSet}
              sizes="(max-width: 700px) 94vw, 672px"
              alt=""
              draggable={false}
              onError={() => setImgFailed(true)}
              className="absolute inset-0 h-full w-full object-cover"
            />
          )}
          {/* The art keeps a dark scrim under the title so the white title and
              the accent eyebrow stay legible over the picture. In dark mode the
              scrim is the card colour, as before. */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent dark:from-card dark:via-card/35" />
          <div className="absolute inset-x-0 bottom-0 flex items-end gap-[clamp(0.6rem,2vw,1rem)] p-[clamp(0.9rem,3vw,1.5rem)]">
            <span
              className="grid size-[clamp(2.5rem,7vw,3.5rem)] shrink-0 place-items-center rounded-xl ring-1 backdrop-blur-md"
              style={{
                color: cover.accent,
                background: `color-mix(in srgb, ${cover.accent} 22%, rgba(12,13,20,0.55))`,
                boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${cover.accent} 45%, transparent)`,
              }}
              aria-hidden
            >
              <Icon className="size-[55%]" />
            </span>
            <div className="min-w-0">
              <p
                className="text-[clamp(0.62rem,1.4vw,0.78rem)] font-semibold uppercase tracking-[0.22em]"
                style={{ color: cover.accent }}
              >
                Game paused · {cover.label}
              </p>
              <h2
                id="pause-card-title"
                data-testid="pause-card-title"
                className="text-[clamp(1.4rem,4.2vw,2.35rem)] font-bold leading-[1.1] text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.7)]"
              >
                {title}
              </h2>
            </div>
          </div>
        </header>

        <div className="overflow-y-auto px-[clamp(0.9rem,3vw,1.5rem)] pt-[clamp(0.25rem,1vw,0.5rem)] text-[clamp(0.9rem,1.9vw,1.05rem)] leading-relaxed">
          {card.kind === 'away' ? (
            <p data-testid="pause-card-away-text">
              You were away for <strong>{formatAway(card.awayMs)}</strong>. Your empire waited for
              you. Nothing happened while the game was paused.
            </p>
          ) : (
            <ul className="space-y-[clamp(0.35rem,1vw,0.55rem)]" data-testid="pause-card-alerts">
              {card.alerts.slice(0, 5).map((a, i) => {
                const AlertIcon = a.kind === 'captured' ? Flag : Swords;
                return (
                  <li
                    key={`${a.kind}:${a.cityId}`}
                    data-testid={`pause-card-alert-${i}`}
                    className="flex items-center gap-3 rounded-lg border border-border/60 bg-muted/30 px-3 py-[clamp(0.4rem,1.2vw,0.6rem)]"
                  >
                    <AlertIcon
                      className="size-[clamp(1rem,2.4vw,1.25rem)] shrink-0"
                      style={{ color: a.kind === 'captured' ? '#f87171' : '#fbbf24' }}
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1">{describeAlert(a)}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      data-testid={`pause-card-goto-${i}`}
                      aria-label={`Go to ${a.cityName}`}
                      onClick={() => onGoTo(a.position)}
                    >
                      <Crosshair className="size-3.5" /> Go to
                    </Button>
                  </li>
                );
              })}
              {card.alerts.length > 5 && (
                <li className="px-3 text-muted-foreground">…and {card.alerts.length - 5} more</li>
              )}
            </ul>
          )}
        </div>

        <footer className="flex flex-col-reverse gap-2 p-[clamp(0.9rem,3vw,1.5rem)] sm:flex-row sm:items-center sm:justify-between">
          <p className="hidden text-xs text-muted-foreground sm:block">
            Change auto-pause in Settings → Gameplay
          </p>
          <Button
            type="button"
            size="lg"
            autoFocus
            data-testid="pause-card-resume"
            data-appdom-id="pause-card-resume"
            data-appdom-role="cta"
            onClick={onResume}
            className="w-full font-semibold text-[#0b0b12] hover:brightness-110 sm:w-auto"
            style={{ background: cover.accent }}
          >
            <Play className="size-4" /> Resume
            <kbd className="ml-1 rounded border border-black/25 px-1.5 text-[0.7em] font-medium opacity-70">
              Space
            </kbd>
          </Button>
        </footer>
      </section>
    </div>
  );
}
